/**
 * Colour parsing and serialisation, per `colorEncoding`.
 *
 * The model stores a structured `Color`, never a '#RRGGBBAA' string, because string colours
 * make equality case-sensitive and that silently breaks dedupe, style coalescing after a
 * strip, and the colour transform. Every format's own encoding is decoded here at the parser
 * and re-encoded here at the writer, and nowhere else.
 *
 * THE ONE THAT BITES: ASS is `&HAABBGGRR` — byte-REVERSED, and `00` is OPAQUE. The model's
 * alpha is 0–1 with 1 = opaque, deliberately inverted so a sign error cannot leak in
 * unnoticed. MicroDVD's `{C:$BBGGRR}` is the same byte order without the alpha byte.
 */

import type { Color } from '../../../contracts/cue-model.ts';

export const WHITE: Color = { r: 255, g: 255, b: 255, a: 1 };
export const BLACK: Color = { r: 0, g: 0, b: 0, a: 1 };

/** The sixteen HTML 4.01 named colours plus the handful that actually appear in `<font
 *  color=…>` inside subtitle files. Not the full CSS list: an unrecognised name is left
 *  unparsed and reported, which is better than guessing at `rebeccapurple`. */
const NAMED: Readonly<Record<string, readonly [number, number, number]>> = {
  black: [0, 0, 0],
  silver: [192, 192, 192],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  white: [255, 255, 255],
  maroon: [128, 0, 0],
  red: [255, 0, 0],
  purple: [128, 0, 128],
  fuchsia: [255, 0, 255],
  magenta: [255, 0, 255],
  green: [0, 128, 0],
  lime: [0, 255, 0],
  olive: [128, 128, 0],
  yellow: [255, 255, 0],
  navy: [0, 0, 128],
  blue: [0, 0, 255],
  teal: [0, 128, 128],
  aqua: [0, 255, 255],
  cyan: [0, 255, 255],
  orange: [255, 165, 0],
};

/** The WebVTT default class colours (`<c.yellow>`), from the spec's ::cue(.yellow) rules. */
export const VTT_CLASS_COLORS: Readonly<Record<string, Color>> = {
  white: { r: 255, g: 255, b: 255, a: 1 },
  lime: { r: 0, g: 255, b: 0, a: 1 },
  cyan: { r: 0, g: 255, b: 255, a: 1 },
  red: { r: 255, g: 0, b: 0, a: 1 },
  yellow: { r: 255, g: 255, b: 0, a: 1 },
  magenta: { r: 255, g: 0, b: 255, a: 1 },
  blue: { r: 0, g: 0, b: 255, a: 1 },
  black: { r: 0, g: 0, b: 0, a: 1 },
};

const HEX = /^[0-9a-fA-F]+$/;

/** CSS-ish colour: `#rgb`, `#rrggbb`, `#rrggbbaa`, or a known name. null when unrecognised. */
export function parseCssColor(raw: string): Color | null {
  const s = raw.trim();
  if (s === '') return null;
  const named = NAMED[s.toLowerCase()];
  if (named !== undefined) return { r: named[0], g: named[1], b: named[2], a: 1 };

  let body = s.startsWith('#') ? s.slice(1) : s;
  // `<font color="&HFF0000">` appears in files converted from ASS by careless tools.
  if (body.startsWith('&H') || body.startsWith('&h')) return parseAssColor(s);
  if (!HEX.test(body)) return null;
  if (body.length === 3) {
    body = `${body[0]}${body[0]}${body[1]}${body[1]}${body[2]}${body[2]}`;
  }
  if (body.length === 6) {
    return {
      r: Number.parseInt(body.slice(0, 2), 16),
      g: Number.parseInt(body.slice(2, 4), 16),
      b: Number.parseInt(body.slice(4, 6), 16),
      a: 1,
    };
  }
  if (body.length === 8) {
    return {
      r: Number.parseInt(body.slice(0, 2), 16),
      g: Number.parseInt(body.slice(2, 4), 16),
      b: Number.parseInt(body.slice(4, 6), 16),
      a: Number.parseInt(body.slice(6, 8), 16) / 255,
    };
  }
  return null;
}

const hex2 = (n: number): string =>
  Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();

/** `#rrggbb`, dropping alpha — the `<font color>` and CSS form. */
export function cssColor(c: Color): string {
  return `#${hex2(c.r)}${hex2(c.g)}${hex2(c.b)}`;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  WebVTT colour classes

  WebVTT has no colour attribute. Colour is a CLASS on a `<c>` span, styled by `::cue(.name)`:
  either one of the eight W3C WebVTT §5.1 default classes, which every `<track>` renderer
  styles with no stylesheet, or a class the file defines in a `STYLE` block (§4.7). The writer
  used to emit `<font color>`, which is not WebVTT and which browsers ignore.
───────────────────────────────────────────────────────────────────────────────────────*/

const VTT_DEFAULT_BY_HEX: ReadonlyMap<string, string> = new Map(
  Object.entries(VTT_CLASS_COLORS).map(([name, c]) => [cssColor(c), name]),
);

/**
 * The class a WebVTT span carries for this colour: the §5.1 default name when the colour is
 * exactly one (`yellow`), otherwise a generated `c_ff8800` that `vttColorRule` defines. Alpha is
 * in neither, as it was not in `<font color>`: `cssColor` drops it.
 */
export function vttColorClass(c: Color): string {
  const hex = cssColor(c);
  return VTT_DEFAULT_BY_HEX.get(hex) ?? `c_${hex.slice(1).toLowerCase()}`;
}

/** The `STYLE` rule a generated colour class needs, or null for a §5.1 default class. */
export function vttColorRule(c: Color): string | null {
  const hex = cssColor(c);
  if (VTT_DEFAULT_BY_HEX.has(hex)) return null;
  const lower = hex.toLowerCase();
  return `::cue(.c_${lower.slice(1)}) { color: ${lower}; }`;
}

export interface VttColorClasses {
  /** Class name → colour: the §5.1 defaults a sheet does not restyle, plus every class a sheet
   *  defines with nothing but an opaque `color`. */
  readonly colors: ReadonlyMap<string, Color>;
  /** A sheet with the rules that became colours taken out; '' when nothing else was in it. */
  readonly strip: (sheet: string) => string;
}

const IDENT = String.raw`-?[_a-zA-Z -￿][-\w -￿]*`;
const CLASS_REF = new RegExp(String.raw`\.(${IDENT})`, 'g');
/** `::cue(.name) { color: X; }` and nothing else, plus the spaces after it on its line. */
const COLOR_RULE = new RegExp(
  String.raw`::cue\(\s*\.(${IDENT})\s*\)\s*\{\s*color\s*:\s*([^;{}]+?)\s*;?\s*\}[ \t]*`,
  'gi',
);
/** A colour a browser reads in a stylesheet. `parseCssColor` also takes bare hex and ASS
 *  `&H…`, which a `<font>` attribute may carry and CSS may not. */
const CSS_COLOR_VALUE = /^(#[0-9a-f]{3}|#[0-9a-f]{6}|[a-z]+)$/i;

/**
 * Which `<c>` classes in a WebVTT file are colours, read from its `STYLE` sheets.
 *
 * A rule is read as a colour only when the class appears NOWHERE ELSE in any sheet, the rule
 * sets `color` and nothing else, the colour is opaque, and the rule is at the top level of a
 * sheet with no comments. Each condition is the line between a class this model can carry as
 * `primaryColor` and one only the stylesheet understands: `.loud` with a bold weight, a class in
 * a compound selector, a rule inside `@media`. Those stay classes and their sheet is kept
 * verbatim, so they round-trip exactly as before.
 *
 * A rule that IS read is taken out of the sheet (`strip`), because the writer regenerates one
 * for every colour it writes. Keeping both would put the rule in the file twice on every round
 * trip. A §5.1 default name that a sheet mentions at all is not a default any more, and is read
 * only through that sheet's rule.
 */
export function readVttColorClasses(sheets: readonly string[]): VttColorClasses {
  const mentions = new Map<string, number>();
  for (const sheet of sheets) {
    for (const m of sheet.matchAll(CLASS_REF)) {
      const name = m[1] as string;
      mentions.set(name, (mentions.get(name) ?? 0) + 1);
    }
  }

  const colors = new Map<string, Color>();
  for (const [name, c] of Object.entries(VTT_CLASS_COLORS)) {
    if (!mentions.has(name)) colors.set(name, c);
  }

  const consumed = new Set<string>();
  for (const sheet of sheets) {
    if (sheet.includes('/*')) continue;
    for (const m of sheet.matchAll(COLOR_RULE)) {
      const name = m[1] as string;
      const value = m[2] as string;
      if (mentions.get(name) !== 1 || depthAt(sheet, m.index) !== 0) continue;
      if (!CSS_COLOR_VALUE.test(value)) continue;
      const c = parseCssColor(value);
      if (c === null || c.a !== 1) continue;
      colors.set(name, c);
      consumed.add(name);
    }
  }

  const strip = (sheet: string): string => {
    if (consumed.size === 0 || sheet.includes('/*')) return sheet;
    const out = sheet.replace(COLOR_RULE, (whole: string, name: string, _v: string, at: number) =>
      consumed.has(name) && depthAt(sheet, at) === 0 ? '' : whole,
    );
    if (out === sheet) return sheet;
    // A sheet has no blank lines (one would end the block), so a line left blank was a rule.
    return out
      .split('\n')
      .filter((l) => l.trim() !== '')
      .join('\n');
  };

  return { colors, strip };
}

/** Brace depth at `at`, so a rule inside `@media { … }` is not read as unconditional. */
function depthAt(sheet: string, at: number): number {
  let depth = 0;
  for (let i = 0; i < at; i++) {
    const ch = sheet[i];
    if (ch === '{') depth++;
    else if (ch === '}') depth--;
  }
  return depth;
}

/**
 * ASS `&HAABBGGRR&` / `&HBBGGRR&` / a bare decimal.
 *
 * Byte-reversed, and the alpha byte is INVERTED relative to the model: ASS `00` is fully
 * opaque. Decimal forms occur in files written by tools that stored the colour as an integer;
 * they are the same 0xAABBGGRR value in base 10.
 */
export function parseAssColor(raw: string): Color | null {
  let s = raw.trim();
  if (s.endsWith('&')) s = s.slice(0, -1);
  if (s.startsWith('&H') || s.startsWith('&h')) s = s.slice(2);
  else if (s.startsWith('H') || s.startsWith('h')) s = s.slice(1);
  else if (/^-?\d+$/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    s = (n >>> 0).toString(16).padStart(8, '0');
  }
  if (s === '' || !HEX.test(s)) return null;

  const v = Number.parseInt(s.padStart(6, '0'), 16);
  const hasAlpha = s.length > 6;
  const b = (v >> 16) & 0xff;
  const g = (v >> 8) & 0xff;
  const r = v & 0xff;
  const aa = hasAlpha ? (v >>> 24) & 0xff : 0;
  return { r, g, b, a: 1 - aa / 255 };
}

/**
 * `&HAABBGGRR` — the STYLE-LINE form, and note the absence of a trailing `&`.
 *
 * Aegisub writes style colours without one and override tags with one (`\c&H0000FF&`). Both
 * parse either way, but a golden file that differs from what Aegisub produces makes every
 * comparison against a real-world file noisy for no reason.
 */
export function assColor(c: Color): string {
  const aa = Math.round((1 - c.a) * 255);
  return `&H${hex2(aa)}${hex2(c.b)}${hex2(c.g)}${hex2(c.r)}`;
}

/** `&HBBGGRR&` — the three-byte override form (`\c&H0000FF&`), which carries no alpha. */
export function assColorNoAlpha(c: Color): string {
  return `&H${hex2(c.b)}${hex2(c.g)}${hex2(c.r)}&`;
}

/** ASS alpha override (`\1a&HFF&`): inverted, so model 1 → `&H00&`. */
export function assAlpha(a: number): string {
  return `&H${hex2(Math.round((1 - a) * 255))}&`;
}

/** MicroDVD `{C:$BBGGRR}` — same byte order as ASS, no alpha byte. */
export function parseMicroDvdColor(raw: string): Color | null {
  const s = raw.trim().replace(/^\$/, '');
  if (!HEX.test(s) || s.length === 0) return null;
  const v = Number.parseInt(s.padStart(6, '0').slice(-6), 16);
  return { r: v & 0xff, g: (v >> 8) & 0xff, b: (v >> 16) & 0xff, a: 1 };
}

export function microDvdColor(c: Color): string {
  return `$${hex2(c.b)}${hex2(c.g)}${hex2(c.r)}`.toLowerCase();
}

export function sameColor(a: Color, b: Color): boolean {
  return a.r === b.r && a.g === b.g && a.b === b.b && a.a === b.a;
}
